import React, {useCallback, useLayoutEffect, useState} from 'react'

interface QueuedRenderProps {
  id: string
  children: React.ReactElement
  queue: ExecutableQueue
  onBeforeRender?: () => void
  showAtOnce?: boolean
  placeholderClassName?: string
}

export const QueuedRender = ({
  id,
  children,
  queue,
  onBeforeRender,
  showAtOnce,
  placeholderClassName
}: QueuedRenderProps) => {
  const [shown, setShown] = useState(false)
  const toShow = shown || !children || !id || showAtOnce
  const show = useCallback(() => !toShow && setShown(true), [])

  useLayoutEffect(() => {
    toShow && onBeforeRender?.()
    return () => {
      !toShow && queue.delete(show)
    }
  }, [toShow])

  const refCallback = (el: HTMLElement) =>
    el && queue.push(id, show, () => !isInViewport(el))

  return toShow ? (
    children
  ) : (
    <span ref={refCallback} className={placeholderClassName ?? 'h-0'} />
  )
}

type QueueItem = {callback: () => void; secondPriority: () => boolean}

export class ExecutableQueue {
  private queue: QueueItem[] = []
  private secondPriorityQueue: QueueItem[][] = [[]]
  private lastTimeout?: NodeJS.Timeout
  private id: string
  private currentBatch = 1
  private updatePrioritiesRequested = false
  private autoStart = true

  constructor(
    private timeout = 0,
    private batchSize = 20
  ) {}

  push(
    id: string,
    callback: QueueItem['callback'],
    secondPriority: () => boolean
  ): void {
    if (id !== this.id) {
      this.clear()
      this.id = id
    }
    this.checkForSecondPriorityQueue()
    this.queue.push({callback, secondPriority})
    this.checkForAutoStart()
  }

  delete(callback: QueueItem['callback']): void {
    const idx = this.queue.findIndex(item => item.callback === callback)
    if (idx >= 0) {
      this.queue.splice(idx, 1)
      return
    }

    this.secondPriorityQueue.some(queue => {
      const idx2 = queue.findIndex(item => item.callback === callback)
      if (idx2 >= 0) {
        queue.splice(idx2, 1)
        return true
      }
      return false
    })
  }

  requestToUpdatePriorities = (): void => {
    this.updatePrioritiesRequested = true
    this.checkForAutoStart()
  }

  private checkForSecondPriorityQueue(): void {
    if (!this.queue.length && this.secondPriorityQueue[0].length) {
      this.secondPriorityQueue.unshift([])
    }
  }

  private checkForAutoStart(): void {
    if (this.autoStart) {
      this.autoStart = false
      setTimeout(() => {
        this.runNext()
      }, 0)
    }
  }

  private scheduleNext(): void {
    if (this.currentBatch < this.batchSize) {
      this.currentBatch++
      this.runNext()
      return
    }

    this.currentBatch = 1
    this.lastTimeout = setTimeout(() => {
      this.runNext()
    }, this.timeout)
  }

  private runNext(): void {
    const next = this.popNext()
    if (!next) {
      this.autoStart = true
      return
    }
    next()
    this.scheduleNext()
  }

  private popNext(): QueueItem['callback'] | undefined {
    while (this.queue.length && !this.updatePrioritiesRequested) {
      const item = this.queue.splice(0, 1)[0]
      if (!item.secondPriority()) {
        return item.callback
      }
      this.secondPriorityQueue[0].push(item)
    }

    if (this.updatePrioritiesRequested) {
      this.updatePriorities()
      this.updatePrioritiesRequested = false
      return this.popNext()
    }

    return undefined
  }

  private updatePriorities(): void {
    this.secondPriorityQueue.forEach(queue => this.queue.push(...queue))
    this.secondPriorityQueue = [[]]
  }

  clear(): void {
    this.queue = []
    this.secondPriorityQueue = [[]]
    this.autoStart = true
    this.clearTimeout()
  }

  private clearTimeout(): void {
    if (this.lastTimeout !== undefined) {
      clearTimeout(this.lastTimeout)
      this.lastTimeout = undefined
    }
  }
}

const xMargin = 10
const yMargin = 100
const minLeftPanelWidth = 47

export const isInViewport = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect()
  return (
    rect.bottom >= -yMargin &&
    rect.right >= minLeftPanelWidth - xMargin &&
    rect.top <=
      yMargin + (window.innerHeight || document.documentElement.clientHeight) &&
    rect.left <=
      xMargin + (window.innerWidth || document.documentElement.clientWidth)
  )
}

export const taskCardPlaceholderClassName =
  'h-64 w-280 block bg-background-card rounded-8 shadow-xs m-0'
